import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { wishlistApi, Character } from '../services/wishlistApi';
import ExcelJS from 'exceljs';


interface RosterPlayer {
  name: string;
  role: 'Tank' | 'Heal' | 'DPS';
  status: 'active' | 'benched';
}

interface BossRoster {
  bossName: string;
  raidDay: string;
  scenario: string;
  activePlayers: RosterPlayer[];
  benchedPlayers: RosterPlayer[];
}

interface OptimizationResult {
  bossName: string;
  raidDay: string;
  scenario: string;
  currentPriority: number;
  optimizedPriority: number;
  suggestedSwaps: Array<{
    remove: string;
    add: string;
    priorityGain: number;
    role?: string;
  }>;
}

const RosterOptimizer: React.FC = () => {
  const { user } = useAuth();
  const [wishlistData, setWishlistData] = useState<Character[]>([]);
  const [rosterData, setRosterData] = useState<BossRoster[]>([]);
  const [optimizationResults, setOptimizationResults] = useState<OptimizationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const fetchWishlistData = async () => {
    setLoading(true);
    setError(null);
    try {
      const characters = await wishlistApi.fetchWishlistData();
      setWishlistData(characters);
    } catch (err) {
      setError('Failed to fetch wishlist data. Please try again.');
      console.error('Error fetching wishlist data:', err);
    } finally {
      setLoading(false);
    }
  };

  const parseExcelData = (jsonData: any[][]): BossRoster[] => {
    
    if (jsonData.length < 2) {
      return [];
    }

    // Find the header row with boss names (skip empty rows)
    let headerRowIndex = 0;
    for (let i = 0; i < Math.min(3, jsonData.length); i++) {
      const row = jsonData[i];
      if (row && row.length > 0 && row[1] && row[1] !== null) {
        headerRowIndex = i;
        break;
      }
    }
    
    const headerRow = jsonData[headerRowIndex];
    
    // Find boss columns (start from column 1, which is Plexus Sentinel)
    const bossColumns: Array<{ name: string; day: string; columnIndex: number }> = [];
    for (let i = 1; i < headerRow.length; i++) {
      const cell = headerRow[i];
      if (cell && cell !== '' && cell !== null) {
        // Determine raid day based on position or content
        // For future-proofing: assume first half of bosses are Wednesday, second half are Thursday
        const totalBosses = headerRow.length - 1; // Subtract 1 for role column
        const midPoint = Math.ceil(totalBosses / 2);
        let day = i <= midPoint ? 'Wednesday' : 'Thursday';
        bossColumns.push({ name: cell, day, columnIndex: i });
      }
    }
    
    // First pass: Identify all players and their roles by scanning role-defining rows
    const playerRoles: { [playerName: string]: 'Tank' | 'Heal' | 'DPS' } = {};
    
    for (let rowIndex = headerRowIndex + 1; rowIndex < jsonData.length; rowIndex++) {
      const row = jsonData[rowIndex];
      if (!row || row.length === 0) continue;
      
      const role = row[0];
      if (role === 'Tank' || role === 'Heal' || role === 'DPS') {
        // This row defines a role, check all boss columns for player names
        for (let bossIndex = 0; bossIndex < bossColumns.length; bossIndex++) {
          const boss = bossColumns[bossIndex];
          const playerName = row[boss.columnIndex];
          if (playerName && playerName !== '' && playerName !== null) {
            playerRoles[playerName] = role as 'Tank' | 'Heal' | 'DPS';
          }
        }
      }
    }
    
    // Parse player data
    const rosterData: BossRoster[] = [];
    
    // Process each boss separately
    bossColumns.forEach(boss => {
      const activePlayers: RosterPlayer[] = [];
      const benchedPlayers: RosterPlayer[] = [];
      
      // Process each row for this specific boss
      for (let rowIndex = headerRowIndex + 1; rowIndex < jsonData.length; rowIndex++) {
        const row = jsonData[rowIndex];
        if (!row || row.length <= boss.columnIndex) continue;
        
        const role = row[0]; // Role is in column 0
        const playerName = row[boss.columnIndex]; // Player name in this boss column
        
        if (!playerName || playerName === '' || playerName === null) continue;
        
        // Determine player role - use pre-identified role if available, otherwise use row role
        let playerRole: 'Tank' | 'Heal' | 'DPS' = 'DPS';
        if (playerRoles[playerName]) {
          playerRole = playerRoles[playerName];
        } else if (role === 'Tank') {
          playerRole = 'Tank';
        } else if (role === 'Heal') {
          playerRole = 'Heal';
        } else if (role === 'DPS') {
          playerRole = 'DPS';
        }
        
        // Determine if player is active or benched for this boss
        if (role === 'Tank' || role === 'Heal' || role === 'DPS') {
          // Player is active for this boss
          activePlayers.push({
            name: playerName,
            role: playerRole,
            status: 'active'
          });
        } else if (role === 'sit' || role === 'absent') {
          // Player is benched for this boss
          benchedPlayers.push({
            name: playerName,
            role: playerRole,
            status: 'benched'
          });
        }
      }
      
      // Determine scenario based on boss assignments
      let scenario = 'Unknown Scenario';
      const totalBosses = bossColumns.length;
      const wednesdayBosses = bossColumns.filter(boss => boss.day === 'Wednesday').length;
      const thursdayBosses = bossColumns.filter(boss => boss.day === 'Thursday').length;
      
      if (totalBosses >= 4) {
        scenario = `${wednesdayBosses} Bosses Wednesday, ${thursdayBosses} Bosses Thursday`;
      } else if (totalBosses === 3) {
        scenario = `${wednesdayBosses} Bosses Wednesday, ${thursdayBosses} Bosses Thursday`;
      } else if (totalBosses === 2) {
        scenario = `${wednesdayBosses} Bosses Wednesday, ${thursdayBosses} Bosses Thursday`;
      } else if (totalBosses === 1) {
        scenario = `1 Boss ${bossColumns[0].day}`;
      }
      
      rosterData.push({
        bossName: boss.name,
        raidDay: boss.day,
        scenario,
        activePlayers,
        benchedPlayers
      });
    });
    
    console.log('Parsed roster data:', rosterData);
    return rosterData;
  };

  const parseFileData = (fileData: string, fileName: string): BossRoster[] => {
    console.log('parseFileData called with file:', fileName, 'data length:', fileData.length);
    
    // Parse CSV data
    const lines = fileData.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    if (lines.length < 2) {
      console.log('Not enough data in CSV file');
      return [];
    }

    // Parse header row to get boss names and raid days
    const headerRow = lines[0].split(',').map(cell => cell.trim());
    console.log('Header row:', headerRow);
    
    // Find boss columns (skip first empty column, start from column 2)
    const bossColumns: Array<{ name: string; day: string; columnIndex: number }> = [];
    for (let i = 2; i < headerRow.length; i++) {
      const cell = headerRow[i];
      if (cell && cell !== '') {
        // Determine raid day based on position or content
        let day = 'Wednesday'; // Default
        if (i >= 5) { // Assuming later columns are Thursday (Araz is Thursday)
          day = 'Thursday';
        }
        bossColumns.push({ name: cell, day, columnIndex: i });
      }
    }
    
    // If no boss columns found in header, try to detect from the data
    if (bossColumns.length === 0) {
      console.log('No boss columns found in header, trying to detect from data...');
      // Look for boss names in the first few rows
      for (let rowIndex = 0; rowIndex < Math.min(3, lines.length); rowIndex++) {
        const row = lines[rowIndex].split(',').map(cell => cell.trim());
        for (let i = 2; i < row.length; i++) {
          const cell = row[i];
          if (cell && cell !== '' && !cell.match(/^(Tank|Heal|DPS|sit|absent)$/)) {
            let day = 'Wednesday';
            if (i >= 5) {
              day = 'Thursday';
            }
            bossColumns.push({ name: cell, day, columnIndex: i });
          }
        }
      }
    }
    
    console.log('Detected boss columns:', bossColumns);
    
    // Parse player data
    const rosterData: BossRoster[] = [];
    
    // Create a roster entry for each boss
    bossColumns.forEach(boss => {
      const activePlayers: RosterPlayer[] = [];
      const benchedPlayers: RosterPlayer[] = [];
      
      // Process each player row
      for (let rowIndex = 2; rowIndex < lines.length; rowIndex++) {
        const row = lines[rowIndex].split(',').map(cell => cell.trim());
        if (row.length <= boss.columnIndex) continue;
        
        const role = row[0]; // Role is in column 0
        const playerName = row[1]; // Player name is in column 1
        const bossAssignment = row[boss.columnIndex]; // Boss assignment in the boss column
        
        if (!playerName || playerName === '') continue;
        
        // Determine player role
        let playerRole: 'Tank' | 'Heal' | 'DPS' = 'DPS';
        
        if (role === 'Tank') {
          playerRole = 'Tank';
        } else if (role === 'Heal') {
          playerRole = 'Heal';
        } else if (role === 'DPS') {
          playerRole = 'DPS';
        }
        
        // Check if player is assigned to this boss
        if (bossAssignment === playerName) {
          // Player is assigned to this boss
          activePlayers.push({
            name: playerName,
            role: playerRole,
            status: 'active'
          });
        } else if (role === 'sit' || role === 'absent') {
          // Player is benched
          benchedPlayers.push({
            name: playerName,
            role: playerRole,
            status: 'benched'
          });
        } else if (role === 'Tank' || role === 'Heal' || role === 'DPS') {
          // Player is not assigned to this boss but is active, add to benched for this boss
          benchedPlayers.push({
            name: playerName,
            role: playerRole,
            status: 'benched'
          });
        }
      }
      
      // Determine scenario based on boss assignments
      let scenario = 'Unknown Scenario';
      const totalBosses = bossColumns.length;
      const wednesdayBosses = bossColumns.filter(boss => boss.day === 'Wednesday').length;
      const thursdayBosses = bossColumns.filter(boss => boss.day === 'Thursday').length;
      
      if (totalBosses >= 4) {
        scenario = `${wednesdayBosses} Bosses Wednesday, ${thursdayBosses} Bosses Thursday`;
      } else if (totalBosses === 3) {
        scenario = `${wednesdayBosses} Bosses Wednesday, ${thursdayBosses} Bosses Thursday`;
      } else if (totalBosses === 2) {
        scenario = `${wednesdayBosses} Bosses Wednesday, ${thursdayBosses} Bosses Thursday`;
      } else if (totalBosses === 1) {
        scenario = `1 Boss ${bossColumns[0].day}`;
      }
      
      rosterData.push({
        bossName: boss.name,
        raidDay: boss.day,
        scenario,
        activePlayers,
        benchedPlayers
      });
    });
    
    console.log('Parsed roster data:', rosterData);
    return rosterData;
  };


  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadedFile(file);
    setRosterData([]); // Clear previous data
    setOptimizationResults([]); // Clear previous results
  };

  const handleSubmit = () => {
    if (!uploadedFile) return;
    
    const fileExtension = uploadedFile.name.split('.').pop()?.toLowerCase();
    
    if (fileExtension === 'csv') {
      // Handle CSV files
      const reader = new FileReader();
      reader.onload = (e) => {
        const csvText = e.target?.result as string;
        const parsedRoster = parseFileData(csvText, uploadedFile.name);
        setRosterData(parsedRoster);
      };
      reader.readAsText(uploadedFile);
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      // Handle Excel files
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const workbook = new ExcelJS.Workbook();
          
          // Load workbook from buffer
          await workbook.xlsx.load(arrayBuffer);
          
          // Process each sheet separately
          let allRosterData: BossRoster[] = [];
          
          workbook.worksheets.forEach((worksheet) => {
            // Convert worksheet to array of arrays (similar to xlsx format)
            const jsonData: any[][] = [];
            
            worksheet.eachRow((row, rowNumber) => {
              const rowData: any[] = [];
              row.eachCell((cell, colNumber) => {
                // Get cell value, handling different types
                let cellValue: any = cell.value;
                
                // Handle rich text, formula results, etc.
                if (cellValue && typeof cellValue === 'object') {
                  if (cellValue.richText) {
                    // Rich text - get plain text
                    cellValue = cellValue.richText.map((rt: any) => rt.text).join('');
                  } else if (cellValue.result !== undefined) {
                    // Formula - get result
                    cellValue = cellValue.result;
                  } else if (cellValue.text !== undefined) {
                    // Hyperlink or other object with text
                    cellValue = cellValue.text;
                  }
                }
                
                rowData[colNumber - 1] = cellValue || null;
              });
              
              // Fill in empty cells with null to match xlsx behavior
              for (let i = rowData.length; i < worksheet.columnCount; i++) {
                rowData[i] = null;
              }
              
              jsonData[rowNumber - 1] = rowData;
            });
            
            const sheetRosterData = parseExcelData(jsonData);
            allRosterData = allRosterData.concat(sheetRosterData);
          });
          
          setRosterData(allRosterData);
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          setError('Failed to parse Excel file. Please check the file format.');
        }
      };
      reader.readAsArrayBuffer(uploadedFile);
    } else {
      setError('Unsupported file format. Please upload a CSV or Excel file.');
    }
  };

  const calculatePlayerPriority = useCallback((playerName: string, bossName: string): number => {
    return wishlistApi.getPlayerPriorityForBoss(playerName, bossName, wishlistData);
  }, [wishlistData]);

  const optimizeRoster = useCallback(() => {
    if (!rosterData.length || !wishlistData.length) {
      return;
    }

    const results: OptimizationResult[] = [];

    rosterData.forEach(roster => {
      const currentPriority = roster.activePlayers.reduce((total, player) => {
        const priority = calculatePlayerPriority(player.name, roster.bossName);
        return total + priority;
      }, 0);

      // Group players by role for proper role-based optimization
      const activeByRole = {
        Tank: roster.activePlayers.filter(p => p.role === 'Tank'),
        Heal: roster.activePlayers.filter(p => p.role === 'Heal'),
        DPS: roster.activePlayers.filter(p => p.role === 'DPS')
      };

      const benchedByRole = {
        Tank: roster.benchedPlayers.filter(p => p.role === 'Tank'),
        Heal: roster.benchedPlayers.filter(p => p.role === 'Heal'),
        DPS: roster.benchedPlayers.filter(p => p.role === 'DPS')
      };

      const suggestedSwaps: Array<{ remove: string; add: string; priorityGain: number; role: string }> = [];
      let optimizedPriority = currentPriority;

      // Optimize each role separately
      Object.keys(activeByRole).forEach(role => {
        const activePlayers = activeByRole[role as keyof typeof activeByRole];
        const benchedPlayers = benchedByRole[role as keyof typeof benchedByRole];

        if (activePlayers.length === 0 || benchedPlayers.length === 0) {
          return;
        }

        // Calculate priorities for this role
        const activePriorities = activePlayers.map(player => ({
          name: player.name,
          priority: calculatePlayerPriority(player.name, roster.bossName)
        })).sort((a, b) => a.priority - b.priority);

        const benchedPriorities = benchedPlayers.map(player => ({
          name: player.name,
          priority: calculatePlayerPriority(player.name, roster.bossName)
        })).sort((a, b) => b.priority - a.priority);

        // Find beneficial swaps within the same role
        for (let i = 0; i < Math.min(benchedPriorities.length, activePriorities.length); i++) {
          const benchedPlayer = benchedPriorities[i];
          const activePlayer = activePriorities[i];
          
          if (benchedPlayer.priority > activePlayer.priority) {
            const gain = benchedPlayer.priority - activePlayer.priority;
            suggestedSwaps.push({
              remove: activePlayer.name,
              add: benchedPlayer.name,
              priorityGain: gain,
              role: role
            });
            optimizedPriority += gain;
          }
        }
      });

      results.push({
        bossName: roster.bossName,
        raidDay: roster.raidDay,
        scenario: roster.scenario,
        currentPriority,
        optimizedPriority,
        suggestedSwaps: suggestedSwaps.map(swap => ({
          remove: swap.remove,
          add: swap.add,
          priorityGain: swap.priorityGain,
          role: swap.role
        }))
      });
    });

    setOptimizationResults(results);
  }, [rosterData, wishlistData, calculatePlayerPriority]);

  useEffect(() => {
    fetchWishlistData();
  }, []);

  useEffect(() => {
    if (rosterData.length > 0 && wishlistData.length > 0) {
      optimizeRoster();
    }
  }, [rosterData, wishlistData, optimizeRoster]);

  // Admin check - must be after all hooks
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-red-500 mb-4">Access Denied</h1>
          <p className="text-gray-300 text-lg">This page is only accessible to guild officers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Roster Optimizer</h1>
          <p className="text-gray-300 text-lg">
            Upload your weekly roster and optimize for maximum loot priority
          </p>
        </div>

        {/* File Upload Section */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-semibold text-white mb-4">Upload Roster</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Upload CSV/Excel file
              </label>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
              />
            </div>
            <div className="text-sm text-gray-400 mb-4">
              <p>Expected format: Player names with roles (Tank/Heal/DPS) and boss assignments</p>
            </div>
            {uploadedFile && (
              <div className="flex items-center gap-4">
                <div className="text-green-400">
                  ✓ File uploaded: {uploadedFile.name}
                </div>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                >
                  Submit & Optimize
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Wishlist Data Section */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-white">Wishlist Data</h2>
            <button
              onClick={fetchWishlistData}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Refresh Data'}
            </button>
          </div>
          
          {error && (
            <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="text-gray-300">
            <p>Characters loaded: {wishlistData.length}</p>
            <p>Last updated: {new Date().toLocaleString()}</p>
          </div>
        </div>

        {/* Player Priority by Boss */}
        {rosterData.length > 0 && wishlistData.length > 0 && (
          <div className="space-y-6 mb-8">
            <h2 className="text-2xl font-semibold text-white">Player Priority by Boss</h2>
            
            {rosterData.map((roster, index) => {
              // Get all players for this boss and their priorities
              const allPlayers = [...roster.activePlayers, ...roster.benchedPlayers];
              const playerPriorities = allPlayers.map(player => ({
                name: player.name,
                role: player.role,
                status: player.status,
                priority: calculatePlayerPriority(player.name, roster.bossName)
              })).sort((a, b) => b.priority - a.priority); // Sort by highest priority first

              return (
                <div key={index} className="bg-gray-800 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-white mb-4">{roster.bossName}</h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="pb-3 text-gray-300 font-semibold">Player</th>
                          <th className="pb-3 text-gray-300 font-semibold">Role</th>
                          <th className="pb-3 text-gray-300 font-semibold">Status</th>
                          <th className="pb-3 text-gray-300 font-semibold text-right">Upgrade %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {playerPriorities.map((player, playerIndex) => (
                          <tr key={playerIndex} className="border-b border-gray-700">
                            <td className="py-2 text-white">{player.name}</td>
                            <td className="py-2">
                              <span className={`text-xs px-2 py-1 rounded ${
                                player.role === 'Tank' ? 'bg-blue-900 text-blue-200' :
                                player.role === 'Heal' ? 'bg-green-900 text-green-200' :
                                'bg-red-900 text-red-200'
                              }`}>
                                {player.role}
                              </span>
                            </td>
                            <td className="py-2">
                              <span className={`text-xs px-2 py-1 rounded ${
                                player.status === 'active' ? 'bg-green-900 text-green-200' :
                                'bg-gray-700 text-gray-300'
                              }`}>
                                {player.status === 'active' ? 'Active' : 'Benched'}
                              </span>
                            </td>
                            <td className="py-2 text-right">
                              <span className={`font-semibold ${
                                player.priority >= 15 ? 'text-red-400' :
                                player.priority >= 10 ? 'text-orange-400' :
                                player.priority >= 5 ? 'text-yellow-400' :
                                'text-gray-400'
                              }`}>
                                {player.priority.toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Optimization Results */}
        {optimizationResults.length > 0 && (
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-white">Optimization Results</h2>
            
            {optimizationResults.map((result, index) => (
                        <div key={index} className="bg-gray-800 rounded-lg p-6">
                          <h5 className="text-xl font-semibold text-white mb-4">{result.bossName}</h5>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Current Priority</h4>
                    <p className="text-2xl font-bold text-white">{result.currentPriority.toFixed(2)}%</p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Optimized Priority</h4>
                    <p className="text-2xl font-bold text-green-400">{result.optimizedPriority.toFixed(2)}%</p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Potential Gain</h4>
                    <p className="text-2xl font-bold text-blue-400">
                      +{(result.optimizedPriority - result.currentPriority).toFixed(2)}%
                    </p>
                  </div>
                </div>

                {result.suggestedSwaps.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-3">Suggested Swaps</h4>
                    <div className="space-y-2">
                      {result.suggestedSwaps.map((swap, swapIndex) => (
                        <div key={swapIndex} className="bg-gray-700 rounded-lg p-3 flex justify-between items-center">
                          <div className="text-white">
                            <span className="text-red-400">Remove: {swap.remove}</span>
                            <span className="mx-2">→</span>
                            <span className="text-green-400">Add: {swap.add}</span>
                            {swap.role && (
                              <span className="ml-2 text-xs bg-gray-600 px-2 py-1 rounded text-gray-300">
                                {swap.role}
                              </span>
                            )}
                          </div>
                          <div className="text-blue-400 font-semibold">
                            +{swap.priorityGain.toFixed(2)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                        </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default RosterOptimizer;
